import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { ACHValidators } from '@arc/aureus';
import { TranslateService } from '@ngx-translate/core';
import {
  AddPaymentMethodForm,
  AllowableCardTypes,
  PaymentMethodType,
} from '../../models';
import { buildRemoteCardCapturedGroup } from '../../helpers';

@Component({
  selector: 'payment-method-form',
  templateUrl: './payment-method-form.component.html',
  styleUrls: ['./payment-method-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodFormComponent
  implements OnInit, AfterViewInit, OnChanges
{
  paymentMethodForm = input.required<AddPaymentMethodForm>();

  acceptAch = input(false);

  acceptCreditCard = input(false);

  showPaymentToggleDefault = input(false);

  cardOverrideEligible = input(false);

  allowableCardBrands = input<AllowableCardTypes[]>();

  userCountry = input<string>();

  showSavePaymentAccount = input(false);

  paymentMethodTypeToggled = output<PaymentMethodType>();

  cardOverrideClick = output<void>();

  useRemoteCardCaptureForm = input(false);

  get creditCardGroup() {
    return this.paymentMethodForm().controls.creditCard;
  }

  get achForm() {
    return this.paymentMethodForm().controls.achInfoDetails;
  }

  get addressGroup() {
    return this.paymentMethodForm().controls.billingInformation.controls
      .address;
  }

  readonly paymentMethodType = PaymentMethodType;
  readonly AchType = 'ach';
  readonly CreditCardType = 'creditCard';

  errorMessages = {
    invalidPostalCode: 'ARC_PAYMENT_INVALID_POSTAL_CODE',
  };

  paymentMethodOptions = [
    {
      label: this.translateService.instant('ARC_PAYMENT_ACH') as string,
      value: PaymentMethodType.ACH,
    },
    {
      label: this.translateService.instant('ARC_PAYMENT_CREDIT') as string,
      value: PaymentMethodType.CREDIT,
    },
  ];

  private get selectedPaymentMethodRef() {
    return this.paymentMethodForm().controls.selectedPaymentMethod;
  }

  get selectedPaymentMethodType() {
    return this.selectedPaymentMethodRef.getRawValue();
  }

  // Track if card override button was clicked
  private cardOverrideClicked = signal(false);

  showPaymentToggle = computed(
    () => this.showPaymentToggleDefault() || this.cardOverrideClicked(),
  );

  showCardOverrideButton = computed(
    () => this.cardOverrideEligible() && !this.cardOverrideClicked(),
  );

  constructor(
    private changeDetector: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.useRemoteCardCaptureForm?.currentValue) {
      this.setCreditCardFormForRemoteCardCapture();
    }
  }

  ngOnInit(): void {
    this.setupForm();
  }

  ngAfterViewInit(): void {
    this.focusFirstInput();
  }

  toggleChanged(): void {
    this.achForm.reset();
    this.creditCardGroup.reset();
    this.updateCountryControl();
    this.emitPaymentMethodTypeToggled();
    if (this.useRemoteCardCaptureForm()) {
      this.setCreditCardFormForRemoteCardCapture();
    }
  }

  onCardOverrideClick(): void {
    this.cardOverrideClicked.set(true);
    this.paymentMethodForm().controls.selectedPaymentMethod.patchValue(
      PaymentMethodType.CREDIT,
    );

    this.addressGroup.controls.country.setValue(this.userCountry());
    this.addressGroup.controls.country.enable();

    this.emitPaymentMethodTypeToggled();
    this.cardOverrideClick.emit();
  }

  isFormValid(): boolean {
    let isPaymentMethodValid = false;
    let isBillingInformationValid = false;
    const selectedPaymentMethodGroup = this.getSelectedPaymentMethodGroup();
    isPaymentMethodValid = selectedPaymentMethodGroup.valid;
    isBillingInformationValid =
      this.paymentMethodForm().controls.billingInformation.valid;

    return isPaymentMethodValid && isBillingInformationValid;
  }

  setPaymentMethodType(value: PaymentMethodType): void {
    this.selectedPaymentMethodRef.patchValue(value);
    this.emitPaymentMethodTypeToggled();
  }

  private setupForm(): void {
    this.setupACHValidators();
    this.setupDefaultPaymentMethod();
    this.updateCountryControl();
  }

  private setupACHValidators(): void {
    if (this.acceptAch()) {
      if (this.userCountry() === 'CA') {
        this.achForm.controls.routingNumber.clearValidators();
        this.achForm.controls.transitNumber.setValidators([
          Validators.required,
          ACHValidators.exactLength(5),
        ]);
        this.achForm.controls.institutionNumber.setValidators([
          Validators.required,
          ACHValidators.exactLength(3),
        ]);
      } else {
        this.achForm.controls.routingNumber.setValidators([
          Validators.required,
          ACHValidators.exactLength(9),
        ]);
        this.achForm.controls.transitNumber.clearValidators();
        this.achForm.controls.institutionNumber.clearValidators();
      }
    }
  }

  private setupDefaultPaymentMethod(): void {
    if (this.acceptAch() && !this.selectedPaymentMethodType) {
      this.setPaymentMethodType(PaymentMethodType.ACH);
    } else if (this.acceptCreditCard() && !this.selectedPaymentMethodType) {
      this.setPaymentMethodType(PaymentMethodType.CREDIT);
    }
  }

  private updateCountryControl(): void {
    if (
      this.selectedPaymentMethodType === PaymentMethodType.ACH &&
      this.userCountry()
    ) {
      this.addressGroup.controls.country.setValue(this.userCountry());
      this.addressGroup.controls.country.disable();
      this.achForm.controls.region.setValue(this.userCountry());
    } else {
      if (!this.addressGroup.controls.country.value) {
        this.addressGroup.controls.country.setValue('');
      }
      this.addressGroup.controls.country.enable();
    }
  }

  private focusFirstInput(): void {
    const element = document.querySelector<HTMLElement>(
      '#paymentToggleWrapper',
    );
    if (element) {
      const inputElements = Array.from(element.getElementsByTagName('input'));
      const firstEnabledInput = inputElements.find(
        (inputEl) => !inputEl.disabled && !inputEl.readOnly,
      );
      if (firstEnabledInput) {
        firstEnabledInput.focus();
        firstEnabledInput.select();
        this.changeDetector.detectChanges();
      }
    }
  }

  private getSelectedPaymentMethodGroup() {
    switch (this.selectedPaymentMethodType) {
      default:
      case PaymentMethodType.ACH:
        return this.achForm;
      case PaymentMethodType.CREDIT:
        return this.creditCardGroup;
    }
  }

  private emitPaymentMethodTypeToggled(): void {
    this.paymentMethodTypeToggled.emit(this.selectedPaymentMethodType);
  }

  private setCreditCardFormForRemoteCardCapture(): void {
    this.paymentMethodForm().setControl(
      'creditCard',
      buildRemoteCardCapturedGroup(),
    );
  }
}
